/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
/* eslint-disable complexity */
import { type OnDestroy, type OnInit, Component, Input } from '@angular/core';
import { type AbstractControl, type ValidationErrors, type ValidatorFn } from '@angular/forms';
import { FormControl, FormGroup, PermissionService, TimeService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { FileSystemModeE, INFINITE_NUM_64, TimeUnitE, wormTypeE } from 'app/common/constants';
import { FileSystem } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { FeatureConfig } from 'app/config/feature';
import { DeviceTimeStrService } from 'app/feature/settings/basic-infomation/device-time/device-time-str.service';
import { type Subscription } from 'rxjs';

import { FileSystemService } from '../../file-system.service';
import { ActionE, FileSystemStrService } from '../../file-system-str.service';
import { DAY_TO_HOUR, HOUR_TO_MINUTE, MONTH_TO_DAY, YEAR_TO_DAY, YEAR_TO_MONTH } from './worm-config-card.const';

export interface WormParam {
    SUBTYPE?: string;
    WORMTYPE?: string | number;
    WORMMINPROTECTPERIOD?: string | number;
    MINPROTECTTIMEUNIT?: string | number;
    WORMMAXPROTECTPERIOD?: string | number;
    MAXPROTECTTIMEUNIT?: string | number;
    WORMDEFPROTECTPERIOD?: string | number;
    DEFPROTECTTIMEUNIT?: string | number;
    WORMAUTOLOCK?: string | boolean;
    WORMAUTOLOCKTIME?: string | number;
    AUTOLOCKTIMEUNIT?: string | number;
    WORMAUTODEL?: string | boolean;
    isWormAuditLogFs?: string | boolean;
}

@Component({
    selector: 'app-worm-config-card',
    templateUrl: './worm-config-card.component.html',
    styleUrls: ['./worm-config-card.component.scss'],
})
export class WormConfigCardComponent implements OnInit, OnDestroy {
    @Input() wormObj: FileSystem;
    @Input() globalClockInitialized: boolean;
    @Input() loading: boolean;
    @Input() action: ActionE;
    @Input() isHyperMetroPairWorm: boolean;

    i18n = this.str.i18n;

    vm: FormGroup;
    errorMessage: any;

    infiniteValue = INFINITE_NUM_64;

    wormTypeDefualtValue = 1;
    minDefualtValue = 3;
    maxDefualtValue = 70;
    defDefualtValue = 70;
    autoLockDefualtValue = 2;

    TIME_WEIGHT = 11315;

    yearOption = {
        label: this.i18n.worm.yearLab,
        value: TimeUnitE.YEAR,
    };
    monthOption = {
        label: this.i18n.worm.monthLab,
        value: TimeUnitE.MONTH,
    };
    dayOption = {
        label: this.i18n.worm.dayLab,
        value: TimeUnitE.DAY,
    };
    hoursOption = {
        label: this.i18n.worm.hoursLab,
        value: TimeUnitE.HOUR,
    };
    minutesOption = {
        label: this.i18n.worm.minutesLab,
        value: TimeUnitE.MINUTE,
    };

    protectOptions: SelectOption[] = [
        this.yearOption, this.monthOption, this.dayOption, this.hoursOption, this.minutesOption,
    ];

    autoLockOptions: SelectOption[] = [
        this.yearOption, this.monthOption, this.dayOption, this.hoursOption, this.minutesOption,
    ];

    // WORM业务类型
    wormTypeOptions: SelectOption[] = [
        {
            label: this.i18n.worm.compliance,
            value: wormTypeE.compliance,
        },
        {
            label: this.i18n.worm.enterprise,
            value: wormTypeE.enterprise,
        },
    ];

    timeStr: string;
    subs$: Subscription[] = [];
    UNPLACEHOLDER = UNPLACEHOLDER;
    wormTypeE = wormTypeE;

    minInitValue: number;
    maxInitValue: number;
    defInitValue: number;
    autoLockInitValue: number;

    /**
     * 是否有时钟的写权限
     */
    hasWormClockW = false;

    /**
     * 最小保护期选中‘无期限’时，最大保护期和默认保护期都选中‘无期限’，且不可修改
     */
    canChange = true;

    SWITCH = SWITCH;
    ActionE = ActionE;
    disableMinMaxProtect = false;

    /**
     * 是否是创建FS
     */
    isCreateFs = false;

    constructor(
        public str: FileSystemStrService,
        private timeService: TimeService,
        public Timestr: DeviceTimeStrService,
        private utilService: UtilService,
        private permissionService: PermissionService,
        private service: FileSystemService,
    ) { }

    ngOnInit(): void {
        this.isCreateFs = this.action === ActionE.Create;
        this.disableMinMaxProtect = FeatureConfig.IsDisableMinMaxProtectInWORM && this.action === ActionE.Modify;
        this.hasWormClockW = this.permissionService.hasPermission('global_secure_compliance_clock_W');

        if (!this.globalClockInitialized) {
            this.initCuccentTime();
        }
        this.getTimeInitialValue();
        this.initVm();
    }

    initCuccentTime(): void {
        this.subs$.push(
            this.timeService.currTimeChanges.subscribe((time) => {
                const timeZoneConfig = this.timeService.getTimeZoneConfig();
                this.timeStr = this.timeService.formatUTC(time * 1000, undefined, true);
                const timeStr = this.timeService.formatUTC(time * 1000, undefined, true);
                const timeZone = this.Timestr.timeZones.find((item) => item.value === timeZoneConfig.timeZoneName);
                const timeZoneStr = timeZone ? timeZone.label.substring(9) : UNPLACEHOLDER;

                this.timeStr = timeStr + timeZoneStr;
            }),
        );
    }

    initVm(): void {
        this.wormObj = this.wormObj || {};
        const minMaxProtectControls = {
            wormMinProtectPeriod: new FormControl(
                {
                    value: this.minInitValue,
                    disabled: this.isInfiniteValue(this.wormObj.WORMMINPROTECTPERIOD),
                },
                {
                    validators: this.rangValidator('wormMinProtectPeriod', 'minProtectPeriodUnit'),
                    updateOn: 'blur',
                },
            ),
            minProtectPeriodUnit: new FormControl({
                value: Number(this.wormObj.MINPROTECTTIMEUNIT) || TimeUnitE.YEAR,
                disabled: this.isInfiniteValue(this.wormObj.WORMMINPROTECTPERIOD),
            }),
            minInfiniteEnable: new FormControl(this.isInfiniteValue(this.wormObj.WORMMINPROTECTPERIOD)),
            wormMaxProtectPeriod: new FormControl(
                {
                    value: this.maxInitValue,
                    disabled: this.isInfiniteValue(this.wormObj.WORMMAXPROTECTPERIOD),
                },
                {
                    validators: [
                        this.rangValidator('wormMaxProtectPeriod', 'maxProtectPeriodUnit'),
                    ],
                    updateOn: 'blur',
                },
            ),
            maxProtectPeriodUnit: new FormControl({
                value: Number(this.wormObj.MAXPROTECTTIMEUNIT) || TimeUnitE.YEAR,
                disabled: this.isInfiniteValue(this.wormObj.WORMMAXPROTECTPERIOD),
            }),
            maxInfiniteEnable: new FormControl(this.isInfiniteValue(this.wormObj.WORMMAXPROTECTPERIOD)),
        };
        this.vm = new FormGroup({
            wormType: new FormControl(Number(this.wormObj.WORMTYPE) || this.wormTypeDefualtValue),
            ...this.disableMinMaxProtect ? {} : { ...minMaxProtectControls },
            wormDefProtectPeriod: new FormControl(
                {
                    value: this.defInitValue,
                    disabled: this.isInfiniteValue(this.wormObj.WORMDEFPROTECTPERIOD),
                },
                {
                    validators: [
                        this.rangValidator('wormDefProtectPeriod', 'defProtectPeriodUnit'),
                        this.defValidator(),
                    ],
                    updateOn: 'change',
                },
            ),
            defProtectPeriodUnit: new FormControl({
                value: Number(this.wormObj.DEFPROTECTTIMEUNIT) || TimeUnitE.YEAR,
                disabled: this.isInfiniteValue(this.wormObj.WORMDEFPROTECTPERIOD),
            }),
            defInfiniteEnable: new FormControl(
                this.isInfiniteValue(this.wormObj.WORMDEFPROTECTPERIOD),
                {
                    validators: [
                        this.defValidator(),
                    ],
                    updateOn: 'change',
                },
            ),
            wormAutoLock: new FormControl(this.utilService.toBool(this.wormObj.WORMAUTOLOCK)),
            wormAutoLockTime: new FormControl(
                this.autoLockInitValue,
                {
                    validators: this.autoLockTimeValidator(),
                    updateOn: 'change',
                },
            ),
            autoLockTimeUnit: new FormControl(Number(this.wormObj.AUTOLOCKTIMEUNIT) || TimeUnitE.HOUR),
            wormAutoDel: new FormControl(this.utilService.toBool(this.wormObj.WORMAUTODEL)),
            // isWormAuditLogFs字段可能取值'0'=未开启，'1'=开启，'255'=无效值(也算未开启)
            isWormAuditLogFs: new FormControl(this.wormObj.isWormAuditLogFs === '1'),
            initWormClock: new FormControl(false),
        });
        this.errorMessage = {
            wormMinProtectPeriod: {
                wormMinProtectPeriod: this.i18n.worm.minRetentionTip,
            },
            wormMaxProtectPeriod: {
                wormMaxProtectPeriod: this.i18n.worm.maxRetentionTip,
            },
            wormDefProtectPeriod: {
                wormDefProtectPeriod: this.i18n.worm.defRetentionTips,
                protectPeriod: this.i18n.worm.timeRangeTips,
            },
            maxInfiniteEnable: {
                protectPeriod: this.i18n.worm.timeRangeTips,
            },
            defInfiniteEnable: {
                protectPeriod: this.i18n.worm.timeRangeTips,
            },
            wormAutoLockTime: {
                wormAutoLockTime: this.i18n.worm.autoLockTips,
            },
        };
        this.getInfiniteStatus();
        this.addCtrlWatch();
    }

    addCtrlWatch(): void {
        this.addMinMaxProtectWatch();
        // 默认值单位或者无期限发生变化时，需要触发默认值的规则校验
        this.vm.get('defProtectPeriodUnit').valueChanges.subscribe(() => {
            this.doDefProtectPeriodValid();
        });
        this.vm.get('defInfiniteEnable').valueChanges.subscribe((newValue: boolean) => {
            if (!newValue) {
                this.doValid(this.vm.get('wormDefProtectPeriod'));
            }
        });
        // 自动锁定单位发生变化时，触发时间输入框的校验
        this.vm.get('autoLockTimeUnit').valueChanges.subscribe(() => {
            this.doValid(this.vm.get('wormAutoLockTime'));
        });
        this.vm.get('wormAutoLock').valueChanges.subscribe((newValue: boolean) => {
            this.setControlStatus(!newValue, 'wormAutoLockTime', 'autoLockTimeUnit');
        });
    }

    addMinMaxProtectWatch(): void {
        // 二级存储不支持修改最大最小保护期
        if (this.disableMinMaxProtect) {
            return;
        }
        // 最小值生变化时，需要触发默认值的规则校验
        this.vm.get('wormMinProtectPeriod').valueChanges.subscribe(() => {
            this.doDefProtectPeriodValid();
        });
        this.vm.get('minProtectPeriodUnit').valueChanges.subscribe(() => {
            this.doValid(this.vm.get('wormMinProtectPeriod'));
            this.doDefProtectPeriodValid();
        });
        // 最小保护期的无期限被选中时，最大保护期和默认保护期同时选中无期限
        this.vm.get('minInfiniteEnable').valueChanges.subscribe((newValue: boolean) => {
            this.setControlStatus(newValue, 'wormMinProtectPeriod', 'minProtectPeriodUnit');
            if (newValue) {
                this.vm.patchValue({ maxInfiniteEnable: true });
                this.vm.patchValue({ defInfiniteEnable: true });
                this.setControlStatus(true, 'wormMaxProtectPeriod', 'maxProtectPeriodUnit');
                this.setControlStatus(true, 'wormDefProtectPeriod', 'defProtectPeriodUnit');
            }
            this.canChange = !newValue;
        });
        // 最大值发生变化时，需要触发默认值的规则校验
        this.vm.get('wormMaxProtectPeriod').valueChanges.subscribe(() => {
            this.doDefProtectPeriodValid();
        });
        this.vm.get('maxProtectPeriodUnit').valueChanges.subscribe(() => {
            this.doValid(this.vm.get('wormMaxProtectPeriod'));
            this.doDefProtectPeriodValid();
        });
        this.vm.get('maxInfiniteEnable').valueChanges.subscribe(() => {
            this.doDefProtectPeriodValid();
        });

    }

    isInfiniteValue(value: string): boolean {
        return value === INFINITE_NUM_64;
    }

    doDefProtectPeriodValid(): void {
        this.doValid(this.vm.get('wormDefProtectPeriod'));
        this.doValid(this.vm.get('defInfiniteEnable'));
    }

    infiniteChanged(newValue: boolean, timeContlName: string, unitContlName: string): void {
        this.setControlStatus(newValue, timeContlName, unitContlName);
    }

    doValid(contrl: AbstractControl): void {
        contrl.markAsTouched();
        contrl.markAsDirty();
        contrl.updateValueAndValidity();
    }

    getTimeInitialValue(): void {
        // 最小保护期，默认3年
        const minTime = this.wormObj ? this.wormObj.WORMMINPROTECTPERIOD : '3';
        this.minInitValue = this.isInvalidNum(minTime) ? this.minDefualtValue : Number(minTime);
        // 最大保护期，默认70年
        const maxTime = this.wormObj ? this.wormObj.WORMMAXPROTECTPERIOD : '70';
        this.maxInitValue = this.isInvalidNum(maxTime) ? this.maxDefualtValue : Number(maxTime);
        // 默认保护期，默认70年
        const defTime = this.wormObj ? this.wormObj.WORMDEFPROTECTPERIOD : '70';
        this.defInitValue = this.isInvalidNum(defTime) ? this.defDefualtValue : Number(defTime);
        // 锁定等待时长，默认2小时
        const autoLockTime = this.wormObj ? this.wormObj.WORMAUTOLOCKTIME : '2';
        this.autoLockInitValue = Number(autoLockTime);

        // 当前版本底层非worm的文件系统，查询结果会带上所有worm的参数，且都是‘0’，和底层商量，这里界面做适配
        // 如果最大是'0'，表示为非worm修改为worm，需要重新赋初始值
        if (maxTime === '0') {
            this.minInitValue = this.minDefualtValue;
            this.maxInitValue = this.maxDefualtValue;
            this.defInitValue = this.defDefualtValue;
            this.autoLockInitValue = this.autoLockDefualtValue;
        }
    }

    isInvalidNum(value: string): boolean {
        return !value || value === INFINITE_NUM_64;
    }

    getInfiniteStatus(): void {
        if (this.wormObj.WORMMINPROTECTPERIOD === INFINITE_NUM_64) {
            this.vm.patchValue({ minInfiniteEnable: true });
        }
        if (this.wormObj.WORMMAXPROTECTPERIOD === INFINITE_NUM_64) {
            this.vm.patchValue({ maxInfiniteEnable: true });
        }
        if (this.wormObj.WORMDEFPROTECTPERIOD === INFINITE_NUM_64) {
            this.vm.patchValue({ defInfiniteEnable: true });
        }
    }

    rangValidator(controlName: string, unitName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            const protectTimeControl = rootCtrl.get(controlName) as FormControl;
            const protectUnitControl = rootCtrl.get(unitName) as FormControl;

            if (!protectTimeControl || !protectUnitControl) {
                return null;
            }

            const protectTime = protectTimeControl.value;
            const protectUnit = protectUnitControl.value;

            const minTimeMonth = this.getMonthTime(0, TimeUnitE.YEAR);
            const manTimeMonth = this.getMonthTime(70, TimeUnitE.YEAR);
            const protectTimeValue = this.getMonthTime(protectTime, protectUnit);

            if (minTimeMonth <= protectTimeValue && protectTimeValue <= manTimeMonth) {
                return null;
            }
            return { [controlName]: true };
        };
    }

    defValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            const minTimeControl = rootCtrl.get('wormMinProtectPeriod') as FormControl;
            const minUnitControl = rootCtrl.get('minProtectPeriodUnit') as FormControl;
            const maxTimeControl = rootCtrl.get('wormMaxProtectPeriod') as FormControl;
            const maxUnitControl = rootCtrl.get('maxProtectPeriodUnit') as FormControl;
            const defTimeControl = rootCtrl.get('wormDefProtectPeriod') as FormControl;
            const defUnitControl = rootCtrl.get('defProtectPeriodUnit') as FormControl;
            const minInfiniteControl = rootCtrl.get('minInfiniteEnable') as FormControl;
            const maxInfiniteControl = rootCtrl.get('maxInfiniteEnable') as FormControl;
            const defInfiniteControl = rootCtrl.get('defInfiniteEnable') as FormControl;

            if (!minTimeControl || !maxTimeControl || !defTimeControl) {
                return null;
            }

            const minTime = minTimeControl.value;
            const minUnit = minUnitControl.value;
            const maxTime = maxTimeControl.value;
            const maxUnit = maxUnitControl.value;
            const defTime = defTimeControl.value;
            const defUnit = defUnitControl.value;
            const minInfiniteValue = minInfiniteControl.value;
            const maxInfiniteValue = maxInfiniteControl.value;
            const defInfiniteValue = defInfiniteControl.value;

            if (!maxInfiniteValue && defInfiniteValue) {
                return { protectPeriod: true };
            }

            const manTimeMonth = this.getMonthTime(70, TimeUnitE.YEAR);

            const minToMonth = minInfiniteValue ? manTimeMonth : this.getMonthTime(minTime, minUnit);
            const maxToMonth = maxInfiniteValue ? manTimeMonth : this.getMonthTime(maxTime, maxUnit);
            const defToMonth = defInfiniteValue ? manTimeMonth : this.getMonthTime(defTime, defUnit);

            if (minToMonth <= defToMonth && defToMonth <= maxToMonth) {
                return null;
            }
            return { protectPeriod: true };
        };
    }

    clearVerification(type: string): void {
        this.vm.get(type).markAsPristine();
        this.vm.get(type).markAsUntouched();
    }

    getMonthTime(time: number, unit: number): number {
        let monthTime = 0;

        switch (unit) {
            // 分钟
            case TimeUnitE.MINUTE:
                monthTime = this.getMinuteUnitMonthTime(time);
                break;
            // 小时
            case TimeUnitE.HOUR:
                monthTime = this.getHourUnitMonthTime(time);
                break;
            // 天
            case TimeUnitE.DAY:
                monthTime = this.getDayUnitMonthTime(time);
                break;
            // 月
            case TimeUnitE.MONTH:
                monthTime = time * this.TIME_WEIGHT * DAY_TO_HOUR * HOUR_TO_MINUTE;
                break;
            // 年
            case TimeUnitE.YEAR:
                monthTime = time * YEAR_TO_MONTH * this.TIME_WEIGHT * DAY_TO_HOUR * HOUR_TO_MINUTE;
                break;
            // 默认
            default:
                monthTime = 0;
        }

        return monthTime;
    }

    /**
     * 单位是分钟的保护期时间（在天的规则基础上 *分钟和小时常数）
     */
    getMinuteUnitMonthTime(time: number): number {
        let monthTime: number;

        if (time > YEAR_TO_DAY * DAY_TO_HOUR * HOUR_TO_MINUTE) {
            monthTime = time * MONTH_TO_DAY * YEAR_TO_MONTH;
        } else if (time === YEAR_TO_DAY * DAY_TO_HOUR * HOUR_TO_MINUTE) {
            monthTime = YEAR_TO_MONTH * this.TIME_WEIGHT * DAY_TO_HOUR * HOUR_TO_MINUTE;
        } else {
            monthTime = time * YEAR_TO_DAY;
        }

        return monthTime;
    }

    /**
     * 单位是小时的保护期时间（在天的规则基础上 *分钟和小时常数）
     */
    getHourUnitMonthTime(time: number): number {
        let monthTime: number;

        if (time > YEAR_TO_DAY * DAY_TO_HOUR) {
            monthTime = time * MONTH_TO_DAY * YEAR_TO_MONTH * HOUR_TO_MINUTE;
        } else if (time === YEAR_TO_DAY * DAY_TO_HOUR) {
            monthTime = YEAR_TO_MONTH * this.TIME_WEIGHT * DAY_TO_HOUR * HOUR_TO_MINUTE;
        } else {
            monthTime = time * YEAR_TO_DAY * HOUR_TO_MINUTE;
        }

        return monthTime;
    }

    /**
     * 单位是天的保护期时间(转换规则：分钟常数*小时常数*天常数*月常数*年常数, 单位是啥，对应常数就换成对应值)
     */
    getDayUnitMonthTime(time: number): number {
        let monthTime: number;

        if (time > YEAR_TO_DAY) {
            monthTime = time * MONTH_TO_DAY * YEAR_TO_MONTH * DAY_TO_HOUR * HOUR_TO_MINUTE;
        } else if (time === YEAR_TO_DAY) {
            monthTime = YEAR_TO_MONTH * this.TIME_WEIGHT * DAY_TO_HOUR * HOUR_TO_MINUTE;
        } else {
            monthTime = time * YEAR_TO_DAY * DAY_TO_HOUR * HOUR_TO_MINUTE;
        }

        return monthTime;
    }

    autoLockTimeValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const rootCtrl = control.root;
            const autoLockTimeControl = rootCtrl.get('wormAutoLockTime') as FormControl;
            const autoLockTimeUnitControl = rootCtrl.get('autoLockTimeUnit') as FormControl;

            if (!autoLockTimeControl || !autoLockTimeUnitControl) {
                return null;
            }

            const autoLockTime = autoLockTimeControl.value;
            const autoLockTimeUnit = autoLockTimeUnitControl.value;

            const maxTime = this.getMaxTime(10, autoLockTimeUnit);

            // 自动锁定时长1分钟~10年
            if (autoLockTime > 0 && autoLockTime <= maxTime) {
                return null;
            } else {
                return { wormAutoLockTime: true };
            }
        };
    }

    getMaxTime(time: number, unit: number): number {
        let targetTime = 0;
        switch (unit) {
            case TimeUnitE.YEAR:
                targetTime = time;
                break;
            case TimeUnitE.MONTH:
                targetTime = time * YEAR_TO_MONTH;
                break;
            case TimeUnitE.DAY:
                targetTime = time * YEAR_TO_DAY;
                break;
            case TimeUnitE.HOUR:
                targetTime = time * YEAR_TO_DAY * DAY_TO_HOUR;
                break;
            default:
                targetTime = time * YEAR_TO_DAY * DAY_TO_HOUR * HOUR_TO_MINUTE;
        }
        return targetTime;
    }

    setControlStatus(status, controlName, timeName): void {
        if (status) {
            this.vm.get(controlName).disable();
            this.vm.get(timeName).disable();
        } else {
            this.vm.get(controlName).enable();
            this.vm.get(timeName).enable();
        }
    }

    getWormParams(): WormParam {
        const wormVmValue = this.vm.value;

        const wormParam: WormParam = {};
        wormParam.SUBTYPE = FileSystemModeE.WORM;
        wormParam.WORMTYPE = this.isHyperMetroPairWorm ? wormTypeE.enterprise : wormVmValue.wormType;

        wormParam.WORMMINPROTECTPERIOD = wormVmValue.minInfiniteEnable ? INFINITE_NUM_64 : wormVmValue.wormMinProtectPeriod;
        wormParam.MINPROTECTTIMEUNIT = wormVmValue.minProtectPeriodUnit;

        wormParam.WORMMAXPROTECTPERIOD = wormVmValue.maxInfiniteEnable ? INFINITE_NUM_64 : wormVmValue.wormMaxProtectPeriod;
        wormParam.MAXPROTECTTIMEUNIT = wormVmValue.maxProtectPeriodUnit;

        wormParam.WORMDEFPROTECTPERIOD = wormVmValue.defInfiniteEnable ? INFINITE_NUM_64 : wormVmValue.wormDefProtectPeriod;
        wormParam.DEFPROTECTTIMEUNIT = wormVmValue.defProtectPeriodUnit;

        wormParam.WORMAUTOLOCK = wormVmValue.wormAutoLock;
        wormParam.WORMAUTOLOCKTIME = wormVmValue.wormAutoLockTime;
        wormParam.AUTOLOCKTIMEUNIT = wormVmValue.autoLockTimeUnit;
        wormParam.WORMAUTODEL = wormVmValue.wormAutoDel;

        // 租户双活 和 模式是企业遵从不支持worm审计日志
        if (!this.isHyperMetroPairWorm && wormVmValue.wormType !== wormTypeE.enterprise) {
            wormParam.isWormAuditLogFs = wormVmValue.isWormAuditLogFs;
        }

        return wormParam;
    }

    getMinMaxProtectParams(changedValue: { [key: string]: any }, vmValue: { [key: string]: any }, wormParam: WormParam): void {
        // x8000不支持修改最小、最大保护期
        if (this.disableMinMaxProtect) {
            return;
        }
        // 时间联动参数，只要有一个发生改变就得把时间和单位同时发下去
        if (Object.hasOwnProperty.call(changedValue, 'wormMinProtectPeriod') ||
            Object.hasOwnProperty.call(changedValue, 'minProtectPeriodUnit') ||
            Object.hasOwnProperty.call(changedValue, 'minInfiniteEnable')) {
            wormParam.WORMMINPROTECTPERIOD = vmValue.minInfiniteEnable ? INFINITE_NUM_64 : vmValue.wormMinProtectPeriod;
            wormParam.MINPROTECTTIMEUNIT = vmValue.minProtectPeriodUnit;
        }

        if (Object.hasOwnProperty.call(changedValue, 'wormMaxProtectPeriod') ||
            Object.hasOwnProperty.call(changedValue, 'maxProtectPeriodUnit') ||
            Object.hasOwnProperty.call(changedValue, 'maxInfiniteEnable')) {
            wormParam.WORMMAXPROTECTPERIOD = vmValue.maxInfiniteEnable ? INFINITE_NUM_64 : vmValue.wormMaxProtectPeriod;
            wormParam.MAXPROTECTTIMEUNIT = vmValue.maxProtectPeriodUnit;
        }
    }

    getChengedWormParam(): WormParam {
        const changedValue: { [key: string]: any } = this.vm.changedRawValue || {};
        const vmValue: { [key: string]: any } = this.vm.value || {};
        const wormParam: WormParam = {};

        this.getMinMaxProtectParams(changedValue, vmValue, wormParam);

        if (Object.hasOwnProperty.call(changedValue, 'wormDefProtectPeriod') ||
            Object.hasOwnProperty.call(changedValue, 'defProtectPeriodUnit') ||
            Object.hasOwnProperty.call(changedValue, 'defInfiniteEnable')) {
            wormParam.WORMDEFPROTECTPERIOD = vmValue.defInfiniteEnable ? INFINITE_NUM_64 : vmValue.wormDefProtectPeriod;
            wormParam.DEFPROTECTTIMEUNIT = vmValue.defProtectPeriodUnit;
        }

        if (Object.hasOwnProperty.call(changedValue, 'wormAutoLock')) {
            wormParam.WORMAUTOLOCK = vmValue.wormAutoLock;
        }

        if (Object.hasOwnProperty.call(changedValue, 'wormAutoLockTime') || Object.hasOwnProperty.call(changedValue, 'autoLockTimeUnit')) {
            wormParam.WORMAUTOLOCKTIME = vmValue.wormAutoLockTime;
            wormParam.AUTOLOCKTIMEUNIT = vmValue.autoLockTimeUnit;
        }

        if (Object.hasOwnProperty.call(changedValue, 'wormAutoDel')) {
            wormParam.WORMAUTODEL = vmValue.wormAutoDel;
        }

        if (Object.hasOwnProperty.call(changedValue, 'isWormAuditLogFs')) {
            wormParam.isWormAuditLogFs = vmValue.isWormAuditLogFs;
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'wormType') && this.isCreateFs) {
            wormParam.WORMTYPE = vmValue.wormType;
        }

        return wormParam;
    }

    /**
     * 更新worm的显示
     */
    updateWormShow(hasHyperMetroPair: boolean): void {
        this.isHyperMetroPairWorm = hasHyperMetroPair;
    }

    isModified(): boolean {
        return this.vm.valueChanged;
    }

    ngOnDestroy(): void {
        for (const sub$ of this.subs$) {
            sub$.unsubscribe();
        }
    }
}
